/**
 * Mapeo de variables de una campaña: qué dato va en cada {{N}}. Puro.
 *
 * La resolución de verdad (quién tiene el dato vacío, qué texto sale) la hace el backend con la
 * misma función para la previa, el chequeo y el envío. Acá sólo se decide si el formulario está
 * completo y si el operador ya eligió qué hacer con los contactos a los que les falta un dato:
 * sin esa decisión, el mensaje saldría con un hueco (CA b).
 *
 * Qué datos existen depende de la audiencia (SPEC-BD D8): una campaña de Activa tiene grupo, oferta y
 * cupón; un público de la base tiene apellido y los campos personalizados de la marca («campo:<clave>»).
 * Por eso las funciones reciben `camposValidos` (lo que devolvió `GET /campos?audiencia=`). Sin él se usa
 * `CAMPOS_VARIABLE`, que es lo de antes.
 */

import type { CampoVariable, Chequeo, MapeoVariables, VariablePlantilla } from "../types";

/** Los datos fijos que existen en alguna audiencia (espejo de `CampoVariableFijo`). */
/** Lo que el backend manda en una variable «dejada vacía» (`VALOR_VACIO` de variables.util): un espacio de ancho cero. */
export const VALOR_VACIO = "\u200B";

export const CAMPOS_VARIABLE: readonly CampoVariable[] = ["nombre", "primerNombre", "apellido", "grupo", "oferta", "vence", "sucursal", "marca", "cupon"];

/** Los datos fijos de un público de la base (backend `CAMPOS_BASE`): no hay grupo, oferta, vence ni cupón. */
export const CAMPOS_BASE_FIJOS: readonly CampoVariable[] = ["nombre", "primerNombre", "apellido", "sucursal", "marca"];

/** Los datos del Público de Activa (backend `CAMPOS_PUBLICO`): el segmento va como «grupo»; no hay oferta, cupón ni apellido. */
export const CAMPOS_PUBLICO_FIJOS: readonly CampoVariable[] = ["nombre", "primerNombre", "grupo", "sucursal", "marca"];

/** Misma regla que la clave de un campo personalizado del backend (`^[a-z][a-z0-9_]{0,39}$`). */
const CAMPO_PERSONALIZADO = /^campo:[a-z][a-z0-9_]{0,39}$/;

/**
 * Los campos que valen para una campaña ya guardada cuando todavía no se trajo `GET /campos` (por ejemplo,
 * al abrir el asistente después de un F5). Activa: los de siempre. Base: los fijos de la base más los
 * «campo:<clave>» que el mapeo guardado ya usa, porque el backend los validó contra el catálogo al guardar.
 * Es sólo para calcular hasta qué paso se puede llegar: el chequeo del backend vuelve a validar todo.
 */
export function camposValidosGuardados(tipo: "activa" | "base" | "publico", m: MapeoVariables | undefined): string[] {
  if (tipo === "publico") return [...CAMPOS_PUBLICO_FIJOS];
  if (tipo !== "base") return [...CAMPOS_VARIABLE];
  const personalizados = Object.values(m || {})
    .map((e) => (e && e.fuente === "campo" ? String(e.campo) : ""))
    .filter((c) => CAMPO_PERSONALIZADO.test(c));
  return [...CAMPOS_BASE_FIJOS, ...new Set(personalizados)];
}

const valido = (campo: string, camposValidos?: readonly string[]) =>
  (camposValidos ?? (CAMPOS_VARIABLE as readonly string[])).includes(campo);

/**
 * Arranque del paso Variables: cada variable con campoSugerido → {fuente:"campo", campo}; sin
 * sugerido → {fuente:"fijo", valor: ejemplo}. Conserva lo que ya estaba en `previo` para las
 * variables que siguen existiendo (las demás se descartan: son de otra plantilla).
 *
 * Con `disponibles` (los campos de la audiencia elegida), un dato que esa audiencia no tiene no se
 * propone: el `grupo` sugerido por la plantilla no existe en un público de la base, y proponerlo dejaría
 * la variable en «Elegí un dato». Va como texto fijo con el ejemplo, igual que una variable sin sugerido.
 * Lo mismo con un `previo` que apuntaba a un dato que la audiencia nueva no tiene.
 */
export function mapeoInicial(variables: VariablePlantilla[], previo?: MapeoVariables, disponibles?: readonly string[]): MapeoVariables {
  const out: MapeoVariables = {};
  const existe = (campo: string) => !disponibles || disponibles.includes(campo);
  for (const v of variables || []) {
    const k = String(v.numero);
    const antes = previo?.[k];
    if (antes && (antes.fuente !== "campo" || existe(antes.campo))) out[k] = antes;
    else if (v.campoSugerido && existe(v.campoSugerido)) out[k] = { fuente: "campo", campo: v.campoSugerido };
    else out[k] = { fuente: "fijo", valor: v.ejemplo || "" };
  }
  return out;
}

/**
 * Errores por variable: fijo vacío → "Escribí el valor"; default vacío → "Escribí el valor por defecto";
 * campo inexistente (o que la audiencia no tiene, si viene `camposValidos`) → "Elegí un dato".
 */
export function validarMapeoLocal(numeros: number[], m: MapeoVariables, camposValidos?: readonly string[]): Record<string, string> {
  const err: Record<string, string> = {};
  for (const n of numeros || []) {
    const k = String(n);
    const e = m?.[k];
    if (!e) { err[k] = "Elegí un dato"; continue; }
    if (e.fuente === "fijo") {
      if (!String(e.valor || "").trim()) err[k] = "Escribí el valor";
      continue;
    }
    if (e.fuente !== "campo" || !valido(e.campo, camposValidos)) { err[k] = "Elegí un dato"; continue; }
    if (e.siVacio?.accion === "default" && !String(e.siVacio.valor || "").trim()) err[k] = "Escribí el valor por defecto";
  }
  return err;
}

/** Hay una entrada por cada variable de la plantilla y cada una es válida (validarMapeoLocal vacío). */
export function mapeoCompleto(numeros: number[], m: MapeoVariables, camposValidos?: readonly string[]): boolean {
  return Object.keys(validarMapeoLocal(numeros, m, camposValidos)).length === 0;
}

/**
 * Variables que el chequeo marcó sin resolver y a las que el operador todavía no les dio decisión
 * (sin `siVacio`). Bloquea el avance (CA b). Una variable pasada a «siempre el mismo texto» ya no
 * depende del contacto, así que deja de contar. Con `camposValidos`, una variable que apunta a un dato
 * que la audiencia no tiene sigue sin decisión aunque tenga `siVacio`: ese dato no va a llegar nunca.
 */
export function sinDecision(
  chequeo: Pick<Chequeo, "variablesSinResolver">,
  m: MapeoVariables,
  camposValidos?: readonly string[],
): { variable: string; campo: CampoVariable; contactos: number }[] {
  return (chequeo?.variablesSinResolver || []).filter((v) => {
    if (!v || !(v.contactos > 0)) return false;
    const e = m?.[v.variable];
    if (!e) return true;
    if (e.fuente === "fijo") return false;
    if (camposValidos && !valido(e.campo, camposValidos)) return true;
    return !e.siVacio;
  });
}
